import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container, Typography, Box, Paper, Divider,
  Button, Avatar, Chip, Grid, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import { 
  ArrowBack as ArrowBackIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import {
  getFirestore, collection, query, where,
  orderBy, getDocs, doc, getDoc, updateDoc, serverTimestamp
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import PayPalButtonStandard from '../components/PayPalButtonStandard';

const BookingHistory = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [fortuneTellers, setFortuneTellers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // 支付相关状态
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  useEffect(() => {
    const fetchBookings = async () => {
      if (!currentUser) {
        navigate('/login');
        return;
      }

      try {
        const db = getFirestore();
        const bookingsRef = collection(db, 'bookings');
        const q = query(
          bookingsRef,
          where('userId', '==', currentUser.uid),
          orderBy('date', 'desc')
        );

        const snapshot = await getDocs(q);
        const bookingsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate() || new Date()
        }));

        setBookings(bookingsData);

        // 获取命理师信息
        const fortuneTellerIds = [...new Set(bookingsData.map(booking => booking.fortuneTellerId))];

        if (fortuneTellerIds.length > 0) {
          const fortuneTellerData = {};

          for (const id of fortuneTellerIds) {
            const ftDoc = await getDoc(doc(db, 'fortuneTellers', id));
            if (ftDoc.exists()) {
              fortuneTellerData[id] = {
                id: ftDoc.id,
                ...ftDoc.data()
              };
            }
          }

          setFortuneTellers(fortuneTellerData);
        }
      } catch (err) {
        console.error('获取预约记录失败:', err);
        setError('获取预约记录失败');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [currentUser, navigate]);

  const handleCancelBooking = async (bookingId) => {
    if (!window.confirm('确定要取消此预约吗？')) {
      return;
    }

    try {
      const db = getFirestore();
      const bookingRef = doc(db, 'bookings', bookingId);

      await updateDoc(bookingRef, {
        status: 'cancelled',
        updatedAt: serverTimestamp()
      });

      // 更新本地状态
      setBookings(prev => prev.map(booking =>
        booking.id === bookingId
          ? { ...booking, status: 'cancelled' }
          : booking
      ));

    } catch (err) {
      console.error('取消预约失败:', err);
      setError('取消预约失败: ' + err.message);
    }
  };

  const handleBackToUser = () => {
    navigate('/user');
  };
  
  // 处理支付按钮点击
  const handlePaymentClick = (booking) => {
    setSelectedBooking(booking);
    setPaymentDialogOpen(true);
    setPaymentSuccess(false);
    setPaymentError('');
  };
  
  // 处理支付成功
  const handlePaymentSuccess = async (paymentDetails) => {
    try {
      console.log('支付成功:', paymentDetails);
      
      if (selectedBooking) {
        const db = getFirestore();
        const bookingRef = doc(db, 'bookings', selectedBooking.id);
        
        // 更新预约状态为已支付/已确认
        await updateDoc(bookingRef, {
          status: 'confirmed',
          paymentStatus: 'completed',
          paymentDetails: paymentDetails,
          updatedAt: serverTimestamp()
        });
        
        // 更新本地状态
        setBookings(prev => prev.map(booking => 
          booking.id === selectedBooking.id 
            ? {...booking, status: 'confirmed', paymentStatus: 'completed'} 
            : booking
        ));
        
        setPaymentSuccess(true);
      }
    } catch (error) {
      console.error('更新支付状态失败:', error);
      setPaymentError(`更新支付状态失败: ${error.message}`);
    }
  };
  
  // 处理支付错误
  const handlePaymentError = (error) => {
    console.error('支付失败:', error);
    setPaymentError(`支付处理出错: ${error.message || '未知错误'}`);
  };
  
  // 关闭支付对话框
  const handleClosePaymentDialog = () => {
    if (paymentSuccess) {
      setPaymentDialogOpen(false);
      setSelectedBooking(null);
    } else {
      setPaymentDialogOpen(false);
    }
  };

  const getStatusText = (status, paymentStatus) => {
    if (status === 'pending' && paymentStatus === 'pending') {
      return '待支付';
    }
    
    switch (status) {
      case 'pending': return '待确认';
      case 'confirmed': return '已确认';
      case 'completed': return '已完成';
      case 'cancelled': return '已取消';
      default: return '未知';
    }
  };

  const formatTimeSlot = (timeSlot) => {
    if (!timeSlot) return '';
    if (Array.isArray(timeSlot)) {
      return timeSlot.join(', ');
    }
    return timeSlot;
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button 
        startIcon={<ArrowBackIcon />}
        onClick={handleBackToUser}
        sx={{ mb: 3 }}
      >
        返回个人中心
      </Button>

      <Typography variant="h4" gutterBottom>预约记录</Typography>

      {error ? (
        <Alert severity="error">{error}</Alert>
      ) : bookings.length === 0 ? (
        <Alert severity="info">暂无预约记录</Alert>
      ) : (
        bookings.map(booking => (
          <Paper key={booking.id} sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle1">
                {fortuneTellers[booking.fortuneTellerId]?.name || '未知命理师'}
              </Typography>
              <Box>
                <Chip 
                  label={getStatusText(booking.status, booking.paymentStatus)} 
                  color={
                    booking.status === 'confirmed' ? 'primary' : 
                    booking.status === 'completed' ? 'success' : 
                    booking.status === 'cancelled' ? 'error' : 
                    (booking.status === 'pending' && booking.paymentStatus === 'pending') ? 'warning' :
                    'default'
                  }
                  size="small"
                />
              </Box>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar 
                src={fortuneTellers[booking.fortuneTellerId]?.photoURL || "https://storage.googleapis.com/destinycrystal-ff557.firebasestorage.app/placeholders/user-placeholder.png"} 
                alt={fortuneTellers[booking.fortuneTellerId]?.name || '未知命理师'}
                sx={{ width: 50, height: 50, mr: 2 }}
              />
              <Box>
                <Typography variant="body2" color="textSecondary">
                  {fortuneTellers[booking.fortuneTellerId]?.title || ''}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {fortuneTellers[booking.fortuneTellerId]?.specialties?.join(', ') || ''}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  预约日期：{booking.date.toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" color="textSecondary">
                  预约时间：{booking.timeSlots 
                              ? formatTimeSlot(booking.timeSlots) 
                              : formatTimeSlot(booking.timeSlot)}
                </Typography>
              </Grid>
              {booking.topic && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    咨询主题：{booking.topic}
                  </Typography>
                </Grid>
              )}
              {booking.note && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    备注：{booking.note}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="textSecondary">
                预约编号：{booking.id}
              </Typography>
              <Box>
                {booking.status === 'pending' && booking.paymentStatus === 'pending' && (
                  <Button 
                    variant="contained" 
                    color="primary"
                    size="small"
                    startIcon={<PaymentIcon />}
                    onClick={() => handlePaymentClick(booking)}
                    sx={{ mr: 1 }}
                  >
                    立即支付
                  </Button>
                )}
                
                {(booking.status === 'pending' || booking.status === 'confirmed') && (
                  <Button 
                    variant="outlined" 
                    color="error"
                    size="small"
                    onClick={() => handleCancelBooking(booking.id)}
                  >
                    取消预约
                  </Button>
                )}
              </Box>
            </Box>
          </Paper>
        ))
      )}
      
      {/* 支付对话框 */}
      <Dialog 
        open={paymentDialogOpen} 
        onClose={handleClosePaymentDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {paymentSuccess 
            ? "支付成功" 
            : `支付预约 - ${fortuneTellers[selectedBooking?.fortuneTellerId]?.name || '占卜师'} 的服务`}
        </DialogTitle>
        <DialogContent>
          {paymentSuccess ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert severity="success" sx={{ mb: 2 }}>支付已成功处理</Alert>
              <Typography variant="body1" gutterBottom>
                您的预约已确认，命理师将在预约时间与您联系。
              </Typography>
              <Typography variant="body2" color="textSecondary">
                您可以在"预约记录"中查看此预约的最新状态。
              </Typography>
            </Box>
          ) : selectedBooking ? (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                  预约详情:
                </Typography>
                <Typography variant="body2">
                  预约编号: {selectedBooking.id}
                </Typography>
                <Typography variant="body2">
                  命理师: {fortuneTellers[selectedBooking.fortuneTellerId]?.name || '未知命理师'}
                </Typography>
                <Typography variant="body2">
                  日期: {selectedBooking.date.toLocaleDateString()}
                </Typography>
                <Typography variant="body2">
                  时间: {selectedBooking.timeSlots 
                    ? formatTimeSlot(selectedBooking.timeSlots) 
                    : formatTimeSlot(selectedBooking.timeSlot)}
                </Typography>
                <Typography variant="body2">
                  时长: {selectedBooking.timeSlots 
                    ? `${selectedBooking.timeSlots.length * 30}分钟` 
                    : "30分钟"}
                </Typography>
                <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                  应付金额: S${selectedBooking.price.toFixed(2)}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              {paymentError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {paymentError}
                </Alert>
              )}
              
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom textAlign="center">
                  选择支付方式
                </Typography>
                <PayPalButtonStandard 
                  amount={selectedBooking.price} 
                  currency="SGD"
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                />
              </Box>
            </Box>
          ) : (
            <Typography>加载中...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePaymentDialog}>
            {paymentSuccess ? "完成" : "关闭"}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BookingHistory;